import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useRemoteComponent } from "@bookingcom/remote-component";
import { customGoal } from "@flights/et-universal";
import { useSelector } from "react-redux";
import { getOrder } from "../../store/order/selectors.js";
import useRouteName from "../../hooks/useRouteName.js";
import useEvents from "../../hooks/useEvents.js";
import useClientMetrics from "../../hooks/useClientMetrics.js";
const REMOTE_COMPONENT_PATH_XM_FLIGHTS = "/SurveyTracker/Flights/Universal";
const SURVEY_CONTAINER_ID_PREFIX = "FlightsSurveyContainer";
var XM_PAGE_NAME;
(function (XM_PAGE_NAME) {
    XM_PAGE_NAME["FlightsCheckoutPage"] = "FlightsCheckoutPage";
    XM_PAGE_NAME["FlightsConfirmationPage"] = "FlightsConfirmationPage";
    XM_PAGE_NAME["FlightsBookingDetailsPage"] = "FlightsBookingDetailsPage";
    XM_PAGE_NAME["FlightsDetailsPage"] = "FlightsDetailsPage";
    XM_PAGE_NAME["FlightsSearchPage"] = "FlightsSearchPage";
    XM_PAGE_NAME["PageClosed"] = "PageClosed";
})(XM_PAGE_NAME || (XM_PAGE_NAME = {}));
const XM_EVENTS = {
    CLIENT_SIDE_SQUEAK: "CAPLA_EVENT_survey-tracker-component-service_CLIENT_SIDE_SQUEAK",
    REMOTE_COMPONENT_ORDER_INFO: "CAPLA_EVENT_xm_remote_component_order_info",
    XM_COMPONENT_INIT: "CAPLA_EVENT_xm_component_init",
    ET_CUSTOM_GOAL: "CAPLA_EVENT_et_custom_goal",
    FLIGHTS_SLOT_INFO: "CAPLA_EVENT_flights_slot_info",
    XM_REMOTE_LOADER_INIT: "CAPLA_EVENT_xm_remote_loader_init"
};
const XM_SURVEY_CONFIG_BY_ROUTENAME = {
    confirmation: {
        XMPageName: XM_PAGE_NAME.FlightsConfirmationPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-confirmation`
    },
    "pb-order-details": {
        XMPageName: XM_PAGE_NAME.FlightsBookingDetailsPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-pb-order-details`
    },
    "checkout-payment": {
        XMPageName: XM_PAGE_NAME.FlightsCheckoutPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-checkout-payment`
    },
    searchresults: {
        XMPageName: XM_PAGE_NAME.FlightsSearchPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-search-results`
    },
    "searchresults-inbound": {},
    "searchresults-outbound": {},
    flightdetails: {
        XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
    },
    "flightdetails-inbound": {
        XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
    },
    "flightdetails-outbound": {
        XMPageName: XM_PAGE_NAME.FlightsDetailsPage,
        containerId: `${SURVEY_CONTAINER_ID_PREFIX}-flight-details`
    }
};
export const SurveyTrackerFlights = ({ rc: Component, error }) => {
    const routeName = useRouteName();
    const order = useSelector(getOrder);
    const findContainerTimer = useRef(0);
    const containerDOMRef = useRef(null);
    const [containerFound, setContainerFound] = useState(false);
    const { grumble, squeak } = useEvents();
    const { trackClientMetric } = useClientMetrics();
    useEffect(() => {
        const sendPageNameInfo = () => {
            if (routeName && containerFound) {
                document.dispatchEvent(new CustomEvent(XM_EVENTS.FLIGHTS_SLOT_INFO, {
                    detail: {
                        page: XM_SURVEY_CONFIG_BY_ROUTENAME[routeName].XMPageName || "",
                        region: containerDOMRef?.current?.getAttribute("data-region")
                    }
                }));
            }
        };
        sendPageNameInfo();
        document.addEventListener(XM_EVENTS.XM_REMOTE_LOADER_INIT, sendPageNameInfo);
        return () => {
            document.dispatchEvent(new CustomEvent(XM_EVENTS.FLIGHTS_SLOT_INFO, {
                detail: { page: XM_PAGE_NAME.PageClosed }
            }));
            document.removeEventListener(XM_EVENTS.XM_REMOTE_LOADER_INIT, sendPageNameInfo);
        };
    }, [routeName, containerFound]);
    useEffect(() => {
        const xmSqueakLogs = (e) => {
            try {
                const data = e.detail;
                if (data) {
                    let squeakString = "xm";
                    data.platform && (squeakString += `_${data.platform}`);
                    data.surveyName && (squeakString += `_${data.surveyName}`);
                    data.action && (squeakString += `_${data.action}`);
                    squeak(squeakString);
                    trackClientMetric(squeakString.toLowerCase());
                }
            }
            catch (e) { }
        };
        document.addEventListener(XM_EVENTS.CLIENT_SIDE_SQUEAK, xmSqueakLogs);
        return () => {
            document.removeEventListener(XM_EVENTS.CLIENT_SIDE_SQUEAK, xmSqueakLogs);
        };
    }, [squeak, trackClientMetric]);
    useEffect(() => {
        const sendOrderInfo = () => {
            if (order) {
                const { orderStatus, orderId } = order;
                document.dispatchEvent(new CustomEvent(XM_EVENTS.REMOTE_COMPONENT_ORDER_INFO, { detail: { orderId, orderStatus } }));
            }
        };
        sendOrderInfo();
        document.addEventListener(XM_EVENTS.XM_COMPONENT_INIT, sendOrderInfo);
        return () => {
            document.removeEventListener(XM_EVENTS.XM_COMPONENT_INIT, sendOrderInfo);
        };
    }, [order]);
    useEffect(() => {
        const xmETCustomGoal = (e) => {
            const data = e.detail;
            if (data && data.experimentTag && data.customGoal) {
                customGoal(data.experimentTag, data.customGoal);
            }
        };
        document.addEventListener(XM_EVENTS.ET_CUSTOM_GOAL, xmETCustomGoal);
        return () => {
            document.removeEventListener(XM_EVENTS.ET_CUSTOM_GOAL, xmETCustomGoal);
        };
    }, []);
    useEffect(() => {
        if (error) {
            grumble(" RC error: Survey tracker wrapper", { routeName, error: error.toString() });
        }
    }, [error, grumble, routeName]);
    const getContainerDom = () => {
        const id = XM_SURVEY_CONFIG_BY_ROUTENAME[routeName]?.containerId;
        if (!id)
            return;
        const elm = window?.document?.getElementById(id);
        if (elm) {
            window?.clearInterval(findContainerTimer.current);
            containerDOMRef.current = elm;
            findContainerTimer.current = 0;
            setContainerFound(true);
        }
        return elm;
    };
    useEffect(() => {
        setContainerFound(false);
        if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
            findContainerTimer.current = window.setInterval(getContainerDom, 200);
        }
        return () => {
            findContainerTimer.current && window.clearInterval(findContainerTimer.current);
        };
    }, [routeName]);
    const survey = useMemo(() => {
        if (containerFound && containerDOMRef.current) {
            return createPortal(React.createElement(Component, null), containerDOMRef.current);
        }
        return (React.createElement("div", { id: "xm-rc-placeholder", style: { height: 0, visibility: "hidden" } },
            React.createElement(Component, null)));
    }, [containerFound, Component]);
    return survey;
};
const SurveyTrackerFlightsUniversal = () => {
    const routeName = useRouteName();
    if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
        return React.createElement(SurveyTrackerFlightsWithLoader, null);
    }
    return null;
};
const SurveyTrackerFlightsWithLoader = () => {
    const { Component, error } = useRemoteComponent({
        project: "web-shell",
        service: "header-mfe",
        path: REMOTE_COMPONENT_PATH_XM_FLIGHTS,
        type: "capla"
    });
    return React.createElement(SurveyTrackerFlights, { rc: Component, error: error });
};
export const SurveyTrackerLoader = (props) => {
    const routeName = useRouteName();
    if (routeName in XM_SURVEY_CONFIG_BY_ROUTENAME) {
        return (React.createElement("div", { id: props.forceId || XM_SURVEY_CONFIG_BY_ROUTENAME[routeName].containerId, "data-region": props.region }));
    }
    return null;
};
export default SurveyTrackerFlightsUniversal;
